import { useMemo } from 'react';

import { PeerParticipantsWith__Type, PeerTeamWith__Type, PeerTypes } from '@givebacks/api/store';
import { ProgressData } from 'core/Components';
import { Card, CardProps, ConditionalWrapper, Flex, Group, Text, UnstyledButton } from 'ui';
import { IconGoal } from 'ui/Icons';

import { CampaignStoreItemTypes } from '../../../api/new/types/utils';
import { CampaignPeerInfoCard, ParticipantTeamProps, showPeerGoal } from './CampaignPeerInfoCard';
import classes from './PeerDonationCard.module.css';

interface PeerDonationCardProps extends CardProps {
  type: PeerTypes;
  peer?: PeerTeamWith__Type | PeerParticipantsWith__Type;
  campaign?: CampaignStoreItemTypes;

  /**
   * Set to true if wanting to fill the full height of a container
   */

  heightAuto?: boolean;
  onClick?: () => void;
  campaignPeerInfoCardProps?: Partial<ParticipantTeamProps>;
}

const PeerDonationCard = ({
  type,
  peer,
  campaign,
  heightAuto = false,
  onClick,
  campaignPeerInfoCardProps,
}: PeerDonationCardProps) => {
  const peerType = peer?.__type;

  const isAthon = campaign?.__type === 'athon';

  const participantGoalType = campaign?.participant_options?.goal_type;

  const noGoalSet = Number(peer?.goal) === 0;

  const isPeerGoalVisible = showPeerGoal(campaign, peer);

  const activityLabel = useMemo(() => {
    if (!isAthon) return;

    if (peerType === 'teams') {
      return `${peer?.activity} ${campaign?.units_of_activity} logged`;
    }

    if (participantGoalType === 'units' && peer?.goal && !noGoalSet) {
      return `${peer.activity} / ${peer.goal} ${campaign?.units_of_activity} goal`;
    } else {
      return `${peer?.activity} ${campaign?.units_of_activity} logged`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAthon, peerType, participantGoalType, peer?.goal, peer?.activity, noGoalSet]);

  return (
    <ConditionalWrapper
      condition={!!onClick}
      wrapper={(children) => (
        <UnstyledButton h="100%" w="100%" onClick={onClick}>
          {children}
        </UnstyledButton>
      )}
    >
      <Card
        p={0}
        bg="cobalt.0"
        className={classes.PeerDonationCard}
        mod={{ ['height-auto']: heightAuto }}
      >
        <CampaignPeerInfoCard
          heightAuto={heightAuto}
          peer_id={peer?.uuid}
          type={type}
          active={!!onClick}
          causeId={campaign?.cause_id}
          campaignId={campaign?.uuid}
          shouldShowAmountRaised={false}
          shouldShowGoal={false}
          shouldShowBackers={false}
          {...campaignPeerInfoCardProps}
        />
        <Flex
          p="md"
          gap="sm"
          direction={!isPeerGoalVisible ? 'row' : 'column'}
          justify="space-between"
        >
          <ProgressData
            size="sm"
            amount_raised={peer?.amount_raised ?? 0}
            goal={isPeerGoalVisible ? Number(peer?.goal) : undefined}
            bg="white"
          />

          {isAthon && (
            <Group gap="xs" mt={!isPeerGoalVisible ? 'lg' : 0}>
              <IconGoal size={16} />
              <Text size="xs">{activityLabel}</Text>
            </Group>
          )}
        </Flex>
      </Card>
    </ConditionalWrapper>
  );
};

export { PeerDonationCard };
